import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../redux/features/auth/authThunks";
import { selectAgent } from "../../redux/features/agent/agentSelectors";
import { selectCateringCheckoutCart } from "../../redux/features/restaurants/restaurantSelectors";
import { HiShoppingCart } from "react-icons/hi";

const Header = ({ headerText }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const agentInfo = useSelector(selectAgent);
  const checkoutCart = useSelector(selectCateringCheckoutCart);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const sidebarRef = useRef(null);
  const menuButtonRef = useRef(null);

  const isLoginPage = location.pathname === "/";
  
  // Define catering-related routes where calendar link should be visible
  const cateringRoutes = [
    "/catering-page",
    "/catering-order",
    "/catering-packages",
    "/catering-checkout",
    "/catering-calendar"
  ];
  
  // Check if current route is a catering route or starts with /catering-package/ (for package detail pages)
  const isCateringRoute = cateringRoutes.includes(location.pathname) || 
                          location.pathname.startsWith("/catering-package/");

  // Calculate total items in cart
  const cartItemCount = checkoutCart?.reduce((total, pkg) => {
    const itemsCount = pkg.items?.length || 0;
    return total + itemsCount;
  }, 0) || 0;

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser());
      toggleSidebar();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setSidebarOpen(false);
      }
    };

    if (sidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarOpen]);

  return (
    <div>
      <header className="relative flex items-center p-4 bg-white border-neutral-500">
        {/* Sidebar Toggle Icon */}
        <img
          ref={menuButtonRef}
          src={process.env.PUBLIC_URL + "/Images/menu.jpg"}
          alt="Menu"
          className="h-3 w-7 cursor-pointer mr-5"
          onClick={toggleSidebar}
        />

        {/* Logo */}
        <Link to="/main-page" className="text-lg font-medium">
          <img
            src="/Images/Logo.png"
            alt="OrderseazenPOS Logo"
            className="h-8 w-auto md:h-12 mr-2"
          />
        </Link>

        {/* Centered Header Text */}
        <h1 className="absolute left-1/2 transform -translate-x-1/2 text-xl font-bold text-gray-500 md:text-2xl whitespace-nowrap">
          {headerText}
        </h1>

        {/* Right side buttons - View Catering Events & Cart Button */}
        {isCateringRoute && (
          <div className="ml-auto flex items-center gap-3 mr-4">
            {/* View Calendar Button - hide on calendar page */}
            {location.pathname !== "/catering-calendar" && (
              <button
                onClick={() => navigate("/catering-calendar")}
                className="flex items-center gap-2 px-4 py-2 bg-amber-800 text-white rounded-lg hover:bg-amber-900 transition-all duration-200 text-sm font-semibold shadow-md hover:shadow-lg"
              >
                <svg 
                  className="w-5 h-5" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                <span className="hidden md:inline">View Calendar</span>
              </button>
            )}

            {/* Cart Button - hide on checkout page */}
            {location.pathname !== "/catering-checkout" && (
              <button
                onClick={() => navigate("/catering-checkout")}
                className="relative flex items-center gap-2 px-4 py-2 bg-amber-800 text-white rounded-lg hover:bg-amber-900 transition-all duration-200 text-sm font-semibold shadow-md hover:shadow-lg"
              >
                <HiShoppingCart className="w-5 h-5" />
                <span className="hidden md:inline">Cart</span>
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </button>
            )}
          </div>
        )}

        {/* Spacer for consistent layout when no buttons */}
        {!isCateringRoute && <div className="ml-auto w-6 hidden md:block"></div>}
      </header>

      {/* Sidebar Menu */}
      {!isLoginPage && (
        <div
          ref={sidebarRef}
          className={`fixed top-0 left-0 h-full bg-white border-r-2 border-neutral-500 z-[9999] shadow-lg transform transition-transform duration-300 ease-in-out ${
            sidebarOpen
              ? "translate-x-0 w-3/4 sm:w-1/2 md:w-1/3 lg:w-1/5"
              : "-translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full p-5">
            {/* Sidebar Header */}
            <div className="flex justify-end">
              <button onClick={() => setSidebarOpen(false)} className="text-xl">
                &times;
              </button>
            </div>

            {/* Agent Info */}
            <div className="text-lg font-medium text-gray-700 mb-4">
              {agentInfo?.first_name} {agentInfo?.last_name}
            </div>

            {/* Sidebar Links */}
            <div className="flex flex-col space-y-4 text-black">
            <Link
                to="/main-page"
                className="text-lg font-medium hover:text-gray-700 transition"
                onClick={() => setSidebarOpen(false)}
              >
                &gt; Home
              </Link>
              {/* <Link
                to="/search"
                className="text-lg font-medium hover:text-gray-700 transition"
              >
                &gt; Place Order
              </Link> */}
              {isCateringRoute && (
                <Link
                  to="/catering-calendar"
                  className="text-lg font-medium hover:text-gray-700 transition"
                  onClick={() => setSidebarOpen(false)}
                >
                  &gt; View Catering Events
                </Link>
              )}
              <Link
                to="/promocode"
                className="text-lg font-medium hover:text-gray-700 transition"
                onClick={() => setSidebarOpen(false)}
              >
                &gt; User Settings
              </Link>
            </div>

            {/* Logout Button */}
            <div className="mt-auto">
              <button
                onClick={handleLogout}
                className="bg-red-700 text-white rounded-xl w-full py-3"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
